import { spacing, typography } from "../app/design-system";
import { FileTextOutlined,MailOutlined,SettingOutlined } from "@ant-design/icons";
import type { FormInstance } from "../../../../../shared/frontend/ui";
import {App,Button,Card,Checkbox,Form,Input,Radio,Select,Typography} from "../../../../../shared/frontend/ui";
import type { TextAreaRef } from "../../../../../shared/frontend/ui";
import React from "react";
import { useFormDirty } from "../app/utils/useFormDirty";
import { useDirtySnapshot } from "../../../../../shared/frontend/forms/useDirtySnapshot";
import { useAuth } from "../app/auth/useAuth";
import { filterChannelsByAvailability,useChannelAvailability } from "../app/integrations/useChannelAvailability";
import { CHANNEL_EMAIL,CHANNEL_EMAIL_LC,CHANNEL_LETTER,CHANNEL_LETTER_LC,CHANNEL_SMS,CHANNEL_WHATSAPP } from "../app/constants/communicationChannel";
import { allStudiosTexts } from "../app/i18n/allStudiosTexts";
import type { LocalizedText } from "../app/i18n/i18n";
import { useI18n } from "../app/i18n/useI18n";
import { buildTemplateVariablePreviewValues,replaceTemplateVariablesWithPreviewValues } from "../app/templates/templateVariablePreviewValues";
import { loadUnits } from "../app/units/unitsStorage";
import { defaultTemplateVariables,filterTemplateVariablesByScope } from "../app/templates/templateVariables";
import type { TemplateVariable } from "../app/templates/templateVariables";
import { useStudioTemplatePreviewValues } from "../app/templates/useStudioTemplatePreviewValues";
import { useStudiosWithAccess } from "../app/utils/useStudiosWithAccess";
import { FormDrawer } from "./FormDrawer";
import {
  deliveryChannelToSendFlags,
  useDeliveryChannelTileOptions,
  type DeliveryChannel
} from "./communication/DeliveryChannelSection";
import { OutcomeTileSelector } from "./OutcomeTileSelector";
import { StudioScopeField } from "./StudioScopeField";
import { TemplateVariableToolbar } from "./TemplateVariableToolbar";

/** Leerer Editorinhalt (serialisiertes leeres Dokument). */
const EMPTY_EDITOR_BODY = "{}";
/** Schätzlänge einer Variablen im SMS-Zeichenzähler. */
const MAX_TEMPLATE_VARIABLE_LENGTH = 60;
// SystemDocumentKind (Backend Domain/Settings/SystemDocumentText.cs)
const SYSTEM_DOCUMENT_KIND_CANCELLATION = 0;
const SYSTEM_DOCUMENT_KIND_REST_PERIOD = 1;
const SYSTEM_DOCUMENT_KIND_PAYMENT_REMINDER = 2;
const SYSTEM_DOCUMENT_KIND_DEBT_COLLECTION = 3;
const SYSTEM_DOCUMENT_KIND_FREE_MESSAGE = 4;

/** Kanalneutraler Auswahlvertrag der drei getrennten Vorlagen-APIs. */
export type CommunicationTextTemplate = {
  id: string;
  key?: string;
  name?: string;
  channel?: string | null;
  usageFlags?: number | null;
  isActive?: boolean;
};

export interface CommunicationComposerProps {
  // Form-Instanz
  form: FormInstance;
  
  // Konfiguration
  mode: "send" | "edit" | "create";
  showEditor?: boolean;
  showTemplateSelection?: boolean;
  showStudioSelection?: boolean;
  showChannelSelection?: boolean;
  showSendingOptions?: boolean;
  showSubject?: boolean;
  showDocumentTitle?: boolean;
  showNameTitle?: boolean;
  
  // Studio-Konfiguration
  studioMode?: "single" | "multiple";
  studioRequired?: boolean;
  studioLabel?: string;
  
  // Channel-Konfiguration
  channelMode?: "single" | "multiple";
  allowedChannels?: string[];
  channelRequired?: boolean;
  
  // Editor-Konfiguration
  editorRows?: number;
  templateVariables?: TemplateVariable[];
  
  // Template-Konfiguration
  /** Vorlagen, die angeboten werden sollen; bekommt die Textvorlage aus der Liste. */
  templateFilter?: (template: CommunicationTextTemplate) => boolean;
  /** Fachlicher Zweck der Textsatz-Auswahl; die Variablen-Combobox wird zusätzlich passend gefiltert. */
  templatePurpose?: "restPeriod" | "cancellation" | "reminder" | "debtCollection" | "contractConfirmation";
  /**
   * Vorlagen dieser Kanäle anbieten (überschreibt die Herleitung aus dem gewählten Versandkanal).
   * Beispiel Lead-Compose: Versand ist immer E-Mail, aber Brief-Vorlagen bleiben wählbar (gehen als PDF-Anhang raus).
   */
  templateChannels?: string[];
  templateLabel?: string;
  templateRequired?: boolean;
  /** Platzhalter für Dokumenttitel (z. B. Ruhezeit: „Ruhezeitbestätigung“, Kündigung: „Kündigungsbestätigung“). */
  documentTitlePlaceholder?: string;
  
  // Callbacks
  onTemplateChange?: (templateId: string | null) => void;
  onChannelChange?: (channel: string | string[]) => void;
  onStudioChange?: (studioId: string | string[] | null) => void;
  onEditorChange?: (body: string) => void;
  onEditorInteraction?: () => void;
  onValuesChange?: (changedValues: Record<string, unknown>, allValues: Record<string, unknown>) => void;
  
  // Initialwerte
  initialStudioId?: string | string[] | null;
  initialChannel?: string | string[] | null;
  initialTemplateId?: string | null;
  
  // Person-spezifisch (für Versand)
  personEmail?: string | null;
  
  // Drawer-Konfiguration
  drawerTitle?: string;
  drawerOpen?: boolean;
  onDrawerClose?: () => void;
  drawerShowEnabledSwitch?: boolean;
  drawerEnabled?: boolean;
  onDrawerEnabledChange?: (enabled: boolean) => void;
  drawerEnabledSwitchLabel?: LocalizedText | false;
  /** Wenn true: Einstellungen (Vorlage, Versand) im gleichen Kontext anzeigen, kein eigener Drawer/Button */
  showSettingsInline?: boolean;
  /** Wenn true, werden Inline-Einstellungen in eine bereits vorhandene äußere Form eingebettet. */
  embeddedInForm?: boolean;

  // Sending Options spezifisch
  showEditBeforeSend?: boolean;
  confirmationLabel?: string;
  showSendConfirmation?: boolean;
  
  // Email validation for LetterSendOptions mode.
  validateEmailTemplate?: boolean;
  emailValidation?: { isValid: boolean; errors: string[] } | null;
  onEmailValidationChange?: (validation: { isValid: boolean; errors: string[] } | null) => void;
}

/** Kanal-Multi-Select mit Bestätigung beim Wechsel SMS ↔ anderer Editor-Typ (Datenverlust-Hinweis). */
const ChannelSelectWithConfirm: React.FC<{
  value?: string[];
  onChange?: (value: string[]) => void;
  form: FormInstance;
  channelOptions: Array<{ value: string; label: string }>;
  placeholder: string;
  onChannelChange?: (channel: string | string[]) => void;
  text: (key: string | { de: string; en: string }) => string;
}> = (props) => {
  const { value, onChange: formOnChange, form, channelOptions, placeholder, onChannelChange, text } = props;
  const { modal } = App.useApp();
  return (
    <Select
      mode="multiple"
      value={value}
      options={channelOptions}
      placeholder={placeholder}
      onChange={(newValue) => {
        const currentChannels = Array.isArray(value) ? value : [value ?? CHANNEL_EMAIL];
        const newChannels = Array.isArray(newValue) ? newValue : [newValue ?? ""];
        const currentIsSms = currentChannels.length === 1 && currentChannels.includes(CHANNEL_SMS);
        const newIsSms = newChannels.length === 1 && newChannels.includes(CHANNEL_SMS);
        const editorTypeChanges = currentIsSms !== newIsSms;
        const body = String(form.getFieldValue("body") ?? "");
        const hasContent = body.trim() !== "" && body !== EMPTY_EDITOR_BODY;
        const applyChannelChange = () => {
          if (newIsSms) {
            const plainText = extractSfdtPlainText(body);
            if (plainText !== body) {
              form.setFieldValue("body", plainText);
            }
          }
          formOnChange?.(newValue);
          onChannelChange?.(newValue);
        };
        if (editorTypeChanges && hasContent) {
          modal.confirm({
            title: text("Channel change"),
            content: text("If you change the channel, the current content may be lost or become invalid. Continue?"),
            okText: text("OK"),
            cancelText: text("Cancel"),
            onOk: applyChannelChange
          });
          return;
        }
        applyChannelChange();
      }}
    />
  );
};

function extractSfdtPlainText(value: string): string {
  const trimmed = String(value || "").trim();
  if (!trimmed.startsWith("{")) return value || "";
  try {
    const root = JSON.parse(trimmed) as Record<string, unknown>;
    const readInlineText = (node: unknown): string => {
      if (!node || typeof node !== "object") return "";
      const record = node as Record<string, unknown>;
      return typeof record.text === "string" ? record.text : "";
    };
    const readBlockText = (node: unknown): string[] => {
      if (node == null) return [];
      if (typeof node !== "object") return [];
      if (Array.isArray(node)) {
        return node.flatMap(readBlockText);
      }
      const record = node as Record<string, unknown>;
      if (Array.isArray(record.inlines)) {
        return [record.inlines.map(readInlineText).join("")];
      }
      if (Array.isArray(record.blocks)) {
        return record.blocks.flatMap(readBlockText);
      }
      if (Array.isArray(record.rows)) {
        return record.rows.flatMap(readBlockText);
      }
      if (Array.isArray(record.cells)) {
        return record.cells.flatMap(readBlockText);
      }
      return [];
    };
    const sectionLines = Array.isArray(root.sections)
      ? root.sections.flatMap((section) => readBlockText((section as Record<string, unknown>).blocks))
      : readBlockText(root);
    return sectionLines
      // Steuerzeichen aus dem Editor-Inhalt entfernen; Tabulator und Zeilenumbruch bleiben absichtlich
      // erhalten. Der Steuerzeichenbereich ist hier der Zweck der Ersetzung, nicht ein Versehen.
      // eslint-disable-next-line no-control-regex
      .map((line) => line.replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, "").trim())
      .filter(Boolean)
      .join("\n\n")
      .replace(/\r\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  } catch {
    return value || "";
  }
}

export const CommunicationComposer: React.FC<CommunicationComposerProps> = ({
  form,
  mode,
  showEditor = false,
  showTemplateSelection = true,
  showStudioSelection = true,
  showChannelSelection = true,
  showSendingOptions = false,
  showSubject: showSubjectProp,
  showDocumentTitle = true,
  showNameTitle = false,
  studioMode = "single",
  studioRequired = true,
  studioLabel,
  channelMode = "single",
  allowedChannels = ["email", "letter"],
  channelRequired = true,
  editorRows = 25,
  templateVariables: templateVariablesProp,
  templateFilter,
  templatePurpose,
  templateChannels,
  templateLabel,
  templateRequired = false,
  documentTitlePlaceholder,
  onTemplateChange,
  onChannelChange,
  onStudioChange,
  onEditorChange,
  onEditorInteraction,
  onValuesChange,
  initialStudioId,
  initialChannel,
  initialTemplateId,
  personEmail,
  drawerTitle,
  drawerOpen: drawerOpenProp,
  onDrawerClose,
  drawerShowEnabledSwitch = false,
  drawerEnabled,
  onDrawerEnabledChange,
  drawerEnabledSwitchLabel = "Enabled",
  showSettingsInline = false,
  embeddedInForm = false,
  showEditBeforeSend = false,
  confirmationLabel,
  showSendConfirmation = false,
  validateEmailTemplate = false,
  emailValidation: emailValidationProp,
  onEmailValidationChange
}) => {
  const auth = useAuth();
  const { text, lang } = useI18n();
  const studioAccess = useStudiosWithAccess();
  
  // State
  const [internalDrawerOpen, setInternalDrawerOpen] = React.useState(false);
  const drawerSnapshotRef = React.useRef<Record<string, unknown> | null>(null);
  const drawerEnabledSnapshotRef = React.useRef<boolean | undefined>(undefined);
  const { dirty: drawerDirty, resetDirty: resetDrawerDirty } = useFormDirty(form);
  const { dirty: enabledDirty, markSaved: markEnabledSaved } = useDirtySnapshot(drawerEnabled);
  const [textTemplates, setTextTemplates] = React.useState<Array<{
    id: string;
    key: string;
    channel: string;
    subject?: string | null;
    documentTitle?: string | null;
    body: string;
    isActive?: boolean;
  }>>([]);
  const [letterSendingIntegrations, setLetterSendingIntegrations] = React.useState<Array<{ id: string; displayName: string }>>([]);
  const [loadingTemplates, setLoadingTemplates] = React.useState(false);
  const [loadingIntegrations, setLoadingIntegrations] = React.useState(false);
  const [internalEmailValidation, setInternalEmailValidation] = React.useState<{ isValid: boolean; errors: string[] } | null>(null);
  const settingsFormId = React.useId();

  // Drawer-Status (extern oder intern gesteuert)
  const drawerOpen = drawerOpenProp !== undefined ? drawerOpenProp : internalDrawerOpen;
  const setDrawerOpen = onDrawerClose ? (open: boolean) => { if (!open) onDrawerClose(); } : setInternalDrawerOpen;

  React.useEffect(() => {
    if (drawerOpen) {
      drawerSnapshotRef.current = form.getFieldsValue(true);
      drawerEnabledSnapshotRef.current = drawerEnabled;
      markEnabledSaved(drawerEnabled);
      resetDrawerDirty();
    } else {
      drawerSnapshotRef.current = null;
    }
    // The opening snapshot must survive ordinary parent field/active-state updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, form, resetDrawerDirty, markEnabledSaved]);

  const handleDrawerCancel = React.useCallback(() => {
    if (drawerSnapshotRef.current) {
      form.resetFields();
      form.setFieldsValue(drawerSnapshotRef.current);
    }
    if (drawerEnabledSnapshotRef.current !== undefined) onDrawerEnabledChange?.(drawerEnabledSnapshotRef.current);
    setDrawerOpen(false);
  }, [form, onDrawerEnabledChange, setDrawerOpen]);

  const handleDrawerApply = React.useCallback(() => {
    setDrawerOpen(false);
  }, [setDrawerOpen]);

  const handleDrawerSubmit = React.useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleDrawerApply();
  }, [handleDrawerApply]);

  // Email-Validierung (extern oder intern)
  const emailValidation = emailValidationProp !== undefined ? emailValidationProp : internalEmailValidation;
  const setEmailValidation = onEmailValidationChange || ((validation: { isValid: boolean; errors: string[] } | null) => {
    setInternalEmailValidation(validation);
  });

  // Form-Watches
  const watchedStudioId = Form.useWatch(studioMode === "single" ? "studioId" : "studioIds", form);
  const watchedChannel = Form.useWatch("channel", form);
  const watchedTemplateId = Form.useWatch(showSendConfirmation ? "letterTemplateId" : "templateId", form);
  const watchedSendEmail = Form.useWatch("sendEmail", form);
  const watchedSendPrint = Form.useWatch("sendPrint", form);
  const watchedIntegrationId = Form.useWatch("integrationId", form);
  // „sendConfirmation" wird hier bewusst nicht beobachtet: gelesen wird das Feld dort, wo es zählt
  // (Form.Item mit shouldUpdate, Zeile ~1186). Der frühere bedingte Form.useWatch verstieß gegen die
  // Hook-Reihenfolge und sein Ergebnis wurde nirgends verwendet.
  const currentSendEmail = watchedSendEmail ?? form.getFieldValue("sendEmail");
  const currentSendPrint = watchedSendPrint ?? form.getFieldValue("sendPrint");
  const currentIntegrationId = watchedIntegrationId ?? form.getFieldValue("integrationId");
  const selectedDeliveryChannel: DeliveryChannel | undefined = currentIntegrationId
    ? `integration:${String(currentIntegrationId)}`
    : currentSendEmail === true
      ? "email"
      : currentSendPrint === true
        ? "print"
        : undefined;
  const deliveryChannelOptions = useDeliveryChannelTileOptions({
    integrations: letterSendingIntegrations,
    personEmail,
    emailValidation,
    validateEmailTemplates: true
  });
  const applyDeliveryChannel = React.useCallback((deliveryChannel: DeliveryChannel) => {
    const flags = deliveryChannelToSendFlags(deliveryChannel);
    const changedValues = {
      sendEmail: flags.sendEmail,
      sendPrint: flags.sendPrint,
      sendApi: flags.sendApi,
      integrationId: flags.integrationId
    };
    form.setFieldsValue(changedValues);
    onValuesChange?.(changedValues, form.getFieldsValue(true));
  }, [form, onValuesChange]);

  // Bestimme ob Subject angezeigt werden soll (muss vor useEffect verwendet werden)
  const showSubject = React.useMemo(() => {
    if (showSubjectProp !== undefined) return showSubjectProp;
    if (mode === "send") {
      const channel = Array.isArray(watchedChannel) ? watchedChannel[0] : watchedChannel;
      const channelKey = String(channel || "").toLowerCase();
      return channelKey === CHANNEL_EMAIL_LC || (channelKey === CHANNEL_LETTER_LC && watchedSendEmail);
    }
    const channels = Array.isArray(watchedChannel) ? watchedChannel : [watchedChannel || CHANNEL_EMAIL];
    return channels.map((channel) => String(channel || "").toLowerCase()).includes(CHANNEL_EMAIL_LC);
  }, [showSubjectProp, mode, watchedChannel, watchedSendEmail]);

  // Initialisiere Form-Werte
  React.useEffect(() => {
    if (initialStudioId !== undefined) {
      if (studioMode === "single") {
        form.setFieldValue("studioId", initialStudioId);
      } else {
        form.setFieldValue("studioIds", Array.isArray(initialStudioId) ? initialStudioId : initialStudioId ? [initialStudioId] : []);
      }
    }
    if (initialChannel !== undefined) {
      form.setFieldValue("channel", initialChannel);
    }
    if (initialTemplateId !== undefined) {
      form.setFieldValue(showSendConfirmation ? "letterTemplateId" : "templateId", initialTemplateId);
    }
  }, []); // Nur einmal beim Mount

  // Lade Briefversand-Integrationen
  React.useEffect(() => {
    if (!showSendingOptions) return;
    
    const rawChannel = watchedChannel ?? form.getFieldValue("channel");
    const channel = Array.isArray(rawChannel) ? rawChannel[0] : rawChannel;
    const isLetter = String(channel || "").toLowerCase().includes("letter");
    
    if (!isLetter) {
      setLetterSendingIntegrations([]);
      return;
    }
    
    const loadIntegrations = async () => {
      setLoadingIntegrations(true);
      try {
        const integrations = await auth.api.get<Array<{ id: string; integrationType: string; displayName: string }>>(
          "/api/v1/integrations/by-capability/LetterSending"
        );
        setLetterSendingIntegrations(integrations || []);
      } catch {
        // Central API toast shows error; keep previous lookup data.
      } finally {
        setLoadingIntegrations(false);
      }
    };
    
    loadIntegrations();
  }, [showSendingOptions, watchedChannel, auth.api, form]);

  // Lade Vorlagen
  React.useEffect(() => {
    if (!showTemplateSelection) return;
    
    const initialSingleStudioId = typeof initialStudioId === "string" ? initialStudioId : null;
    const studioId = studioMode === "single"
      ? ((watchedStudioId as string | null | undefined) || initialSingleStudioId)
      : (Array.isArray(watchedStudioId) && watchedStudioId.length > 0 ? watchedStudioId[0] : null);
    
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const initialSingleChannel = typeof initialChannel === "string" ? initialChannel : null;
        const channel = (Array.isArray(watchedChannel) ? watchedChannel[0] : watchedChannel) || initialSingleChannel;
        const wantedChannel = String(channel || "").toLowerCase();
        const explicitTemplateChannels = (templateChannels ?? []).map((c) => c.toLowerCase()).filter(Boolean);
        if (!wantedChannel && explicitTemplateChannels.length === 0) {
          setTextTemplates([]);
          return;
        }
        const requestedChannels = explicitTemplateChannels.length > 0 ? explicitTemplateChannels : [wantedChannel];
        const wantsSms = requestedChannels.some((entry) => entry.includes("sms"));
        const wantsWhatsApp = requestedChannels.some((entry) => entry.includes("whatsapp"));
        const systemDocumentTextsUrl = templatePurpose === "contractConfirmation"
          ? "/api/v1/system-document-texts?purpose=ContractConfirmation"
          : `/api/v1/system-document-texts?documentKind=${templatePurpose === "cancellation"
            ? SYSTEM_DOCUMENT_KIND_CANCELLATION
            : templatePurpose === "restPeriod"
              ? SYSTEM_DOCUMENT_KIND_REST_PERIOD
              : templatePurpose === "reminder"
                ? SYSTEM_DOCUMENT_KIND_PAYMENT_REMINDER
                : templatePurpose === "debtCollection"
                  ? SYSTEM_DOCUMENT_KIND_DEBT_COLLECTION
                  : SYSTEM_DOCUMENT_KIND_FREE_MESSAGE}`;
        const templates = wantsSms
          ? (await auth.api.get<Array<{ id: string; name: string; body: string; studioIds?: string[]; isActive: boolean }>>("/api/v1/sms-templates"))
              .filter((entry) => entry.isActive && (!studioId || !entry.studioIds?.length || entry.studioIds.includes(studioId)))
              .map((entry) => ({ id: entry.id, key: entry.name, channel: CHANNEL_SMS, body: entry.body, isActive: true }))
          : wantsWhatsApp
            ? (await auth.api.get<Array<{ id: string; name: string; studioIds?: string[]; isActive: boolean; bindings: Array<{ bodyPreview?: string | null }> }>>("/api/v1/whatsapp-templates"))
                .filter((entry) => entry.isActive && (!studioId || !entry.studioIds?.length || entry.studioIds.includes(studioId)))
                .map((entry) => ({ id: entry.id, key: entry.name, channel: CHANNEL_WHATSAPP, body: entry.bindings[0]?.bodyPreview ?? "", isActive: true }))
            : (await auth.api.get<Array<{ id?: string | null; variantName?: string | null; channel: string[]; subject?: string | null; salutation?: string | null; introduction?: string | null; notice?: string | null; closing?: string | null; studioId?: string | null }>>(
                systemDocumentTextsUrl
              ))
                .filter((entry) => entry.id && (!studioId || !entry.studioId || entry.studioId === studioId)
                  && entry.channel.some((entryChannel) => requestedChannels.some((requested) => entryChannel.toLowerCase() === requested)))
                .map((entry) => ({
                  id: entry.id!,
                  key: entry.variantName || text("Base case"),
                  channel: entry.channel.join(","),
                  subject: entry.subject,
                  documentTitle: entry.subject,
                  body: [entry.salutation, entry.introduction, entry.notice, entry.closing].filter(Boolean).join("\n\n"),
                  isActive: true,
                }));

        const channelFiltered = templates;
        const availableTemplates = templateFilter ? channelFiltered.filter(templateFilter) : channelFiltered;

        setTextTemplates(availableTemplates);

        // Der Formularwert muss immer zu einer tatsächlich angebotenen Option gehören.
        //
        const templateFieldName = showSendConfirmation ? "letterTemplateId" : "templateId";
        const currentValue = form.getFieldValue(templateFieldName);
        const currentTemplateExists = availableTemplates.some(template => template.id === currentValue);
        if (!currentTemplateExists) {
          const defaultTemplate = availableTemplates.length === 1 ? availableTemplates[0] : undefined;
          if (defaultTemplate) {
            form.setFieldValue(templateFieldName, defaultTemplate.id);
            onTemplateChange?.(defaultTemplate.id);
          } else if (currentValue) {
            form.setFieldValue(templateFieldName, null);
            onTemplateChange?.(null);
          }
        }
      } catch {
        // Central API toast shows error; keep previous templates instead of showing a false empty list.
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, [
    showTemplateSelection,
    studioMode,
    watchedStudioId,
    watchedChannel,
    templateFilter,
    templatePurpose,
    templateChannels,
    initialStudioId,
    initialChannel,
    auth.api,
    form,
    showSendConfirmation,
    onTemplateChange
  ]);

  // Übernehme Vorlagen-Metadaten und lade bei sichtbarem Editor den Vorlageninhalt.
  React.useEffect(() => {
    if (!watchedTemplateId || !textTemplates.length) return;

    const selectedTemplate = textTemplates.find(t => t.id === watchedTemplateId);
    if (!selectedTemplate) return;

    // Setze Subject und DocumentTitle
    if (showSubject !== false) {
      form.setFieldValue("subject", selectedTemplate.subject || "");
    }
    if (showDocumentTitle) {
      form.setFieldValue("documentTitle", selectedTemplate.documentTitle || selectedTemplate.subject || selectedTemplate.key || "");
    }

    if (!showEditor) return;

    const body = selectedTemplate.body || "";
    if (body) {
      form.setFieldValue("body", body);
      onEditorChange?.(body);
    }
  }, [watchedTemplateId, textTemplates, showEditor, form, onEditorChange, showSubject, showDocumentTitle]);

  // Ein Systemdokument trägt sein E-Mail-Erscheinungsbild selbst. Die alte zweite Validierung
  // gegen eine unabhängige E-Mail-Hüllenvorlage entfällt mit deren Entfernung.
  React.useEffect(() => {
    if (!validateEmailTemplate || !showSendConfirmation) return;
    setEmailValidation(watchedTemplateId ? { isValid: true, errors: [] } : null);
  }, [validateEmailTemplate, showSendConfirmation, watchedTemplateId, watchedStudioId, studioMode, auth.api, form]);

  // Initialisiere sendConfirmation auf true, falls noch nicht gesetzt
  React.useEffect(() => {
    if (showSendConfirmation) {
      const currentValue = form.getFieldValue("sendConfirmation");
      if (currentValue === undefined || currentValue === null) {
        form.setFieldValue("sendConfirmation", true);
      }
    }
  }, [showSendConfirmation, form]);

  // Vorlagen sind nach Kanal getrennt; frei pflegbare Kategorien gibt es nicht mehr.
  const templateOptions = React.useMemo(() => {
    if (!showTemplateSelection) return [];
    return textTemplates.map((t) => ({
      value: t.id,
      label: (
        <span>
          {t.channel?.toLowerCase().includes("email") && <MailOutlined style={{ marginRight: spacing.controlCompact }} />}
          {t.channel?.toLowerCase().includes("letter") && <FileTextOutlined style={{ marginRight: spacing.controlCompact }} />}
          {t.key}
        </span>
      )
    }));
  }, [textTemplates, showTemplateSelection]);

  // Channel-Optionen: SMS und WhatsApp nur mit konfigurierter Integration.
  const channelAvailability = useChannelAvailability();
  const channelOptions = React.useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    const useTemplateChannelValues = mode === "create" || mode === "edit";
    const availableChannels = filterChannelsByAvailability(allowedChannels, channelAvailability.canSend);
    if (availableChannels.includes("email")) {
      options.push({ value: useTemplateChannelValues ? CHANNEL_EMAIL : CHANNEL_EMAIL_LC, label: text("Email") });
    }
    if (availableChannels.includes("letter")) {
      options.push({ value: useTemplateChannelValues ? CHANNEL_LETTER : CHANNEL_LETTER_LC, label: text("Letter") });
    }
    if (availableChannels.includes("sms")) {
      options.push({ value: CHANNEL_SMS, label: text("SMS") });
    }
    if (availableChannels.includes("whatsapp")) {
      options.push({ value: CHANNEL_WHATSAPP, label: text("WhatsApp") });
    }
    return options;
  }, [allowedChannels, channelAvailability.canSend, mode, text]);

  // Template-Feldname
  const templateFieldName = showSendConfirmation ? "letterTemplateId" : "templateId";

  // Nur reiner SMS-Kanal hat eine bearbeitbare Textfläche; Dokumente entstehen aus den
  // aktiven Systemdokument-Arbeitsflächen.
  const isSmsEditor = React.useMemo(() => {
    const channels = Array.isArray(watchedChannel) ? watchedChannel : [watchedChannel || CHANNEL_EMAIL];
    return channels.length === 1 && channels.includes(CHANNEL_SMS);
  }, [watchedChannel]);

  // Memoize templateVariables to ensure it's only accessed when needed.
  // Gefiltert nach Briefart: im Mahnbrief haben Ruhezeit-Variablen nichts zu suchen, im
  // Kündigungsschreiben keine Termin-Variablen. Ohne Briefart bleibt der volle Pool.
  const templateVariables = React.useMemo(() => {
    let pool: TemplateVariable[];
    if (templateVariablesProp) {
      pool = templateVariablesProp;
    } else {
      try {
        pool = defaultTemplateVariables || [];
      } catch {
        pool = [];
      }
    }
    return filterTemplateVariablesByScope(pool, templatePurpose ?? null);
  }, [templatePurpose, templateVariablesProp]);

  // SMS nutzt denselben zentralen Variablenkatalog wie die anderen Kanäle.
  const [smsPreviewValuesEnabled, setSmsPreviewValuesEnabled] = React.useState(false);
  const smsBodyRef = React.useRef<TextAreaRef>(null);

  React.useEffect(() => {
    setSmsPreviewValuesEnabled(false);
  }, [isSmsEditor]);

  const smsTemplateVariables = React.useMemo<TemplateVariable[]>(() => {
    return templateVariables;
  }, [templateVariables]);

  const watchedBody = Form.useWatch("body", form);
  const selectedStudioIdsForPreview = React.useMemo(
    () => Array.isArray(watchedStudioId) ? watchedStudioId : (watchedStudioId ? [String(watchedStudioId)] : []),
    [watchedStudioId]
  );
  const studioPreviewValues = useStudioTemplatePreviewValues(selectedStudioIdsForPreview);
  // Beträge in der Vorschau folgen der Einstellung unter Einheiten — sonst zeigte die Leiste eine
  // Schreibweise, die die erzeugte Nachricht nicht hat.
  const previewUnits = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);
  const smsPreviewValues = React.useMemo(
    () => buildTemplateVariablePreviewValues(smsTemplateVariables, lang, studioPreviewValues, previewUnits),
    [lang, previewUnits, smsTemplateVariables, studioPreviewValues]
  );

  const smsPreviewBody = React.useMemo(
    () => replaceTemplateVariablesWithPreviewValues(String(watchedBody ?? ""), smsPreviewValues),
    [smsPreviewValues, watchedBody]
  );
  const smsCharCount = React.useMemo(() => {
    const body = String(watchedBody ?? "");
    const staticPart = body.replace(/\{\{[^}]+\}\}/g, "");
    const minLen = staticPart.length;
    const matches = body.match(/\{\{[^}]+\}\}/g) || [];
    const varCount = matches.length;
    const maxVarLen = MAX_TEMPLATE_VARIABLE_LENGTH;
    const maxLen = minLen + varCount * maxVarLen;
    return { minLen, maxLen, hasVariables: varCount > 0 };
  }, [watchedBody]);

  return (
    <div className="communication-composer">
      {/* SMS ist der einzige verbleibende direkte Texteingabepfad. */}
      {showEditor && isSmsEditor && (
        <Card className="communication-composer-editor-card">
          <Form form={form} layout="vertical" requiredMark={false} onValuesChange={onValuesChange}>
            <Form.Item
              name="body"
              rules={mode === "send" || mode === "create" || mode === "edit" ? [{ required: true, message: text("Text is required") }] : []}
            >
              <>
                <TemplateVariableToolbar
                  variables={smsTemplateVariables}
                  value={String(form.getFieldValue("body") || "")}
                  onChange={(next) => {
                    form.setFieldValue("body", next);
                    onEditorInteraction?.();
                    onEditorChange?.(next);
                  }}
                  textAreaRef={smsBodyRef}
                  previewEnabled={smsPreviewValuesEnabled}
                  onPreviewEnabledChange={setSmsPreviewValuesEnabled}
                  extra={
                    <span style={{ color: "var(--ant-color-text-secondary)", fontSize: `${typography.fontSize.sm}px` }}>
                      {smsCharCount.hasVariables
                        ? text("{0}–{1} characters").replace("{0}", String(smsCharCount.minLen)).replace("{1}", String(smsCharCount.maxLen))
                        : text("{0} characters").replace("{0}", String(smsCharCount.minLen))}
                    </span>
                  }
                />
                <Input.TextArea
                  ref={smsBodyRef}
                  rows={editorRows || 8}
                  placeholder={text("SMS text (max. 160 characters recommended). Variables: {{variable}}")}
                  maxLength={500}
                  readOnly={smsPreviewValuesEnabled}
                  value={smsPreviewValuesEnabled ? smsPreviewBody : form.getFieldValue("body") || ""}
                  onChange={(e) => {
                    if (smsPreviewValuesEnabled) return;
                    form.setFieldValue("body", e.target.value);
                    onEditorInteraction?.();
                    onEditorChange?.(e.target.value || "");
                  }}
                />
              </>
            </Form.Item>
          </Form>
        </Card>
      )}

      {/* Einstellungen-Button (nur wenn nicht inline und Drawer nicht extern gesteuert) */}
      {!showSettingsInline && drawerOpenProp === undefined && (
        <div className="communication-composer-settings-trigger" style={{ marginTop: spacing.lg }}>
          <Button icon={<SettingOutlined />} onClick={() => setDrawerOpen(true)}>
            {text("Settings")}
          </Button>
        </div>
      )}

      {/* Einstellungen: inline oder im Drawer */}
      {(() => {
        const settingsFormBody = (
          <>
          {/* Studio-Auswahl: immer ganz oben */}
          {showStudioSelection && (
            studioMode === "single" ? (
              <Form.Item
                name="studioId"
                label={studioLabel || text(allStudiosTexts.labelAllowedStudios)}
                rules={studioRequired ? [{ required: true, message: text("Studio is required.") }] : []}
              >
                <Select
                  placeholder={text(allStudiosTexts.placeholderSelectStudios)}
                  options={studioAccess.optionsOnlyAllowed}
                  onChange={(value) => {
                    onStudioChange?.(value);
                  }}
                />
              </Form.Item>
            ) : (
              <StudioScopeField
                show={studioAccess.showStudioSelection}
                name="studioIds"
                isEntryAllStudios={(form.getFieldValue("studioIds") ?? []).length === 0}
                optionsOnlyAllowed={studioAccess.optionsOnlyAllowed}
                isRestrictedUser={studioAccess.isRestrictedUser}
                label={studioLabel || allStudiosTexts.labelAllowedStudios}
                tooltip={allStudiosTexts.tooltipEmptyMulti}
                placeholder={allStudiosTexts.placeholderNoSelection}
                required={studioRequired}
                rules={studioRequired ? [{ required: true, message: text("Studio is required.") }] : []}
                showSearch
                maxTagCount="responsive"
                onChange={(value) => onStudioChange?.(value)}
              />
            )
          )}

          {/* Bestätigung versenden (nur bei LetterSendOptions-Modus) */}
          {showSendConfirmation && (
            <>
              <Form.Item name="sendConfirmation" valuePropName="checked" initialValue={true}>
                <Checkbox>
                  {confirmationLabel || text("Send confirmation")}
                </Checkbox>
              </Form.Item>
            </>
          )}

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => {
              if (showSendConfirmation) {
                const prev = prevValues?.sendConfirmation ?? true;
                const curr = currentValues?.sendConfirmation ?? true;
                return prev !== curr;
              }
              return false;
            }}
          >
            {({ getFieldValue }) => {
              const sendConfirmation = showSendConfirmation ? (getFieldValue("sendConfirmation") ?? true) : true;
              if (!sendConfirmation) return null;

              return (
                <>
          {/* Channel-Auswahl */}
          {showChannelSelection && (
            <Form.Item
              name="channel"
              label={text(channelMode === "multiple" ? "Channel (label)" : "Type (label)")}
              rules={channelRequired ? [{ required: true, message: text("Channel is required") }] : []}
            >
              {channelMode === "single" ? (
                <Radio.Group
                  onChange={(e) => {
                    onChannelChange?.(e.target.value);
                  }}
                >
                  {channelOptions.map((opt) => (
                    <Radio key={opt.value} value={opt.value}>
                      {opt.value.toLowerCase() === CHANNEL_EMAIL_LC && <MailOutlined style={{ marginRight: spacing.controlCompact }} />}
                      {opt.value.toLowerCase() === CHANNEL_LETTER_LC && <FileTextOutlined style={{ marginRight: spacing.controlCompact }} />}
                      {opt.label}
                    </Radio>
                  ))}
                </Radio.Group>
              ) : (
                <ChannelSelectWithConfirm
                  form={form}
                  channelOptions={channelOptions}
                  placeholder={text("Select channel")}
                  onChannelChange={onChannelChange}
                  text={text}
                />
              )}
            </Form.Item>
          )}

          {/* Vorlagenauswahl */}
          {showTemplateSelection && (
            <Form.Item
              name={templateFieldName}
              label={templateLabel || text("Text template")}
              rules={templateRequired ? [{ required: true, message: text("Template is required") }] : []}
            >
              <Select
                allowClear
                loading={loadingTemplates}
                placeholder={text("Select template…")}
                showSearch
                filterOption={(input, option) => {
                  const label = typeof option?.label === "object" 
                    ? ((option.label as { props?: { children?: unknown[] } })?.props?.children?.[1] as string) || ""
                    : String(option?.label || "");
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                options={templateOptions as React.ComponentProps<typeof Select>["options"]}
                onChange={(value) => {
                  onTemplateChange?.(value || null);
                  if (!value) {
                    if (showSubject !== false) form.setFieldValue("subject", "");
                    if (showDocumentTitle) form.setFieldValue("documentTitle", "");
                    form.setFieldValue("body", "");
                  }
                }}
              />
            </Form.Item>
          )}

          {/* Name/Titel (nur bei create/edit) */}
          {showNameTitle && (
            <Form.Item
              name="key"
              label={text("Name/Title")}
              rules={[{ required: true, message: text("Name/Title is required") }]}
            >
              <Input placeholder={text("e.g. Welcome Email")} />
            </Form.Item>
          )}

          {/* Versandoptionen (nur bei Brief und mode="send") */}
          {showSendingOptions && (() => {
            // Fallback auf getFieldValue: Im finalDocument-Modus ist das channel-Feld nur im Store
            // gesetzt, aber nicht als Form.Item gerendert → Form.useWatch liefert undefined. Ohne
            // Fallback bliebe der Versandoptionen-Block (und damit der Einstellungen-Drawer) leer.
            const rawChannel = watchedChannel ?? form.getFieldValue("channel");
            const channel = Array.isArray(rawChannel) ? rawChannel[0] : rawChannel;
            if (String(channel || "").toLowerCase() !== CHANNEL_LETTER_LC) return null;
            
            return (
              <Form.Item label={text("Delivery channel")}>
                <OutcomeTileSelector<DeliveryChannel>
                  value={selectedDeliveryChannel}
                  options={deliveryChannelOptions}
                  onChange={applyDeliveryChannel}
                />
                {loadingIntegrations ? (
                  <Typography.Text type="secondary">{text("Loading…")}</Typography.Text>
                ) : null}
              </Form.Item>
            );
          })()}

          {/* "Noch bearbeiten" Checkbox */}
          {showEditBeforeSend && (
            <Form.Item name="editBeforeSend" valuePropName="checked">
              <Checkbox>
                {text("Edit before sending")}
              </Checkbox>
            </Form.Item>
          )}

          {/* Subject */}
          {showSubject && (
            <Form.Item
              name="subject"
              label={text("Subject")}
              rules={mode === "create" || mode === "edit" ? [{ required: true, message: text("Subject is required") }] : []}
            >
              <Input placeholder={text("Email subject")} />
            </Form.Item>
          )}

          {/* DocumentTitle */}
          {showDocumentTitle && (
            <Form.Item
              name="documentTitle"
              label={text("Document title (digital file)")}
              tooltip={text("Will be displayed as label in the digital file. Variables like {{person.firstName}} will be replaced.")}
            >
              <Input placeholder={documentTitlePlaceholder ?? text("e.g. Document title - {{person.firstName}} {{person.lastName}}")} />
            </Form.Item>
          )}
                </>
              );
            }}
          </Form.Item>
          </>
        );
        return showSettingsInline ? (
          <div className="communication-composer-settings-inline" style={{ marginTop: spacing.lg }}>
            {embeddedInForm ? settingsFormBody : (
              <Form form={form} layout="vertical" requiredMark={false} onValuesChange={onValuesChange}>
                {settingsFormBody}
              </Form>
            )}
          </div>
        ) : (
          <FormDrawer
            title={drawerTitle || "Settings"}
            dirty={drawerDirty || enabledDirty}
            open={drawerOpen}
            onCancel={handleDrawerCancel}
            formId={settingsFormId}
            submitText="Apply"
            showEnabledSwitch={drawerShowEnabledSwitch}
            enabled={drawerEnabled}
            onEnabledChange={onDrawerEnabledChange}
            enabledSwitchLabel={drawerEnabledSwitchLabel}
          >
            <Form
              id={settingsFormId}
              form={form}
              layout="vertical"
              requiredMark={false}
              onSubmitCapture={handleDrawerSubmit}
              onValuesChange={onValuesChange}
            >
              {settingsFormBody}
            </Form>
          </FormDrawer>
        );
      })()}
    </div>
  );
};
